import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ComicGrid, { SortOption } from "./components/layout/ComicGrid";
import AddComicModal from "./components/modals/AddComicModal";
import EditComicModal from "./components/modals/EditComicModal";
import SettingsModal from "./components/modals/SettingsModal";
import MaintenanceModal from "./components/modals/MaintenanceModal";
import AboutModal from "./components/modals/AboutModal";
import LLMConfigurationsModal from "./components/modals/LLMConfigurationsModal";
import ListEditor from "./components/forms/ListEditor";
import ListView from "./components/layout/ListView";
import Login from "./components/pages/Login";
import FirstTimeSetup from "./components/pages/FirstTimeSetup";
import PasswordChangeModal from "./components/modals/PasswordChangeModal";
import FieldVisibilityModal from "./components/modals/FieldVisibilityModal";
import NotFound from "./components/pages/NotFound";
import PublicListView from "./components/layout/PublicListView";
import ThemeToggle from "./components/ui/ThemeToggle";
import FloatingHeader from "./components/layout/FloatingHeader";
import ConfettiAnimation from "./components/ui/ConfettiAnimation";
import { Comic, CustomList } from "./types";
import {
    getComic,
    deleteCustomList,
    copyCustomList,
    isAuthenticated,
    checkUsersExist,
} from "./api";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PublicThemeProvider } from "./contexts/PublicThemeProvider";
import { SettingsProvider, useSettings } from "./contexts/SettingsContext";
import { FieldVisibilityProvider, useFieldVisibility } from "./contexts/FieldVisibilityContext";
import { ReferenceDataProvider, useReferenceData } from "./contexts/ReferenceDataContext";
import { ComicsDataProvider, useComicsData } from "./contexts/ComicsDataContext";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { getWebroot } from "./utils/webroot";

// Helper function to render menu items
function renderMenuItems(
    t: any,
    setIsSettingsModalOpen: (open: boolean) => void,
    setIsLLMConfigModalOpen: (open: boolean) => void,
    setIsMaintenanceModalOpen: (open: boolean) => void,
    setIsAboutModalOpen: (open: boolean) => void,
    setIsPasswordChangeModalOpen: (open: boolean) => void,
    setIsFieldVisibilityModalOpen: (open: boolean) => void,
    handleLogout: () => void,
    setIsMenuOpen: (open: boolean) => void
) {
    return (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border dark:border-gray-700">
            <div className="px-4 py-2 border-b dark:border-gray-700">
                <ThemeToggle onThemeChange={() => setIsMenuOpen(false)} />
            </div>
            <button
                onClick={() => {
                    setIsSettingsModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                </svg>
                {t('settings.settings')}
            </button>
            <button
                onClick={() => {
                    setIsFieldVisibilityModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                </svg>
                {t('settings.fieldVisibility')}
            </button>
            <button
                onClick={() => {
                    setIsLLMConfigModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
                {t('llm.configurations.title')}
            </button>
            <button
                onClick={() => {
                    setIsPasswordChangeModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 16l-4 4H3v-4l4-4 2.257-2.257A6 6 0 0117 7z"
                    />
                </svg>
                {t('auth.changePassword')}
            </button>
            <button
                onClick={() => {
                    setIsMaintenanceModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m0 6h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m0 6h9.75M7.5 18a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0m-3.75 0H7.5"
                    />
                </svg>
                {t('settings.maintenance')}
            </button>
            <button
                onClick={() => {
                    setIsAboutModalOpen(true);
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
                {t('about.about')}
            </button>
            <button
                onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
                <svg
                    className="h-4 w-4 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                </svg>
                {t('settings.logout')}
            </button>
        </div>
    );
}

// Comics page component
function ComicsPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { refreshSettings } = useSettings();
    
    // Update document meta tags based on current language
    useDocumentMeta();
    const { comics, setComics, fetchComics, fetchLists, loading } = useComicsData();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isListEditorOpen, setIsListEditorOpen] = useState(false);
    const [editingComic, setEditingComic] = useState<Comic | null>(null);
    const [editingList, setEditingList] = useState<CustomList | null>(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [settingsVersion, setSettingsVersion] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
    const [isFieldVisibilityModalOpen, setIsFieldVisibilityModalOpen] = useState(false);
    const [isLLMConfigModalOpen, setIsLLMConfigModalOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState<'all' | 'owned' | 'wishlist' | 'ordered' | 'loaned'>('all');
    // Load saved sort order from localStorage, fallback to 'title'
    const [sortBy, setSortBy] = useState<SortOption>(() => {
        const savedSortBy = localStorage.getItem('comicSortBy');
        return (savedSortBy as SortOption) || 'title';
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Check if the click is outside the menu
            if (
                menuRef.current &&
                !menuRef.current.contains(target)
            ) {
                setIsMenuOpen(false);
            }
            
            // Also prevent closing if clicking on select dropdown or its options
            if (target.tagName === 'SELECT' || target.tagName === 'OPTION') {
                return;
            }
        };

        if (isMenuOpen) {
            // Use setTimeout to delay the event listener attachment
            // This prevents conflicts with the menu opening click
            const timeoutId = setTimeout(() => {
                document.addEventListener("click", handleClickOutside);
            }, 10);
            
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener("click", handleClickOutside);
            };
        }
    }, [isMenuOpen]);

    // Save sort order to localStorage when it changes and refetch if needed
    useEffect(() => {
        const previousSortBy = localStorage.getItem('comicSortBy');
        localStorage.setItem('comicSortBy', sortBy);
        
        // If switching to/from custom order, refetch comics with appropriate ordering
        if (sortBy === 'custom' && previousSortBy !== 'custom') {
            fetchComics(true);
        } else if (sortBy !== 'custom' && previousSortBy === 'custom') {
            fetchComics(false);
        }
    }, [sortBy, fetchComics]);


    const handleComicAdded = () => {
        // Fetch both comics and lists to ensure dynamic lists include new comics
        fetchComics(sortBy === 'custom');
        fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
        setIsAddModalOpen(false);
    };

    const handleEditClick = async (comic: Comic) => {
        try {
            // Fetch the comic with role information
            const comicWithRoles = await getComic(comic.id);
            setEditingComic(comicWithRoles);
            setIsEditModalOpen(true);
        } catch (error) {
            
            // Fallback to the comic without roles
            setEditingComic(comic);
            setIsEditModalOpen(true);
        }
    };

    const handleComicUpdated = (shouldShowConfetti?: boolean) => {
        // Fetch both comics and lists to ensure dynamic lists reflect changes
        fetchComics(sortBy === 'custom');
        fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
        setIsEditModalOpen(false);
        setEditingComic(null);
        
        // Trigger confetti if requested
        if (shouldShowConfetti) {
            setShowConfetti(true);
        }
    };

    const handleCustomReorder = () => {
        // Refresh comics with custom order after reordering
        fetchComics(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setEditingComic(null);
    };

    const handleLogout = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        setComics([]);
        window.location.reload();
    };

    const handleSettingsChanged = async () => {
        try {
            // Refresh settings through the context
            await refreshSettings();
        } catch (error) {
            
        }

        setSettingsVersion((prev) => prev + 1);
    };

    const handleListSaved = (savedListId?: number) => {
        fetchLists();
        // Navigate to the newly created/saved list
        if (savedListId) {
            navigate(`/lists/${savedListId}`);
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-lg text-gray-900 dark:text-gray-100">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    const handleViewChange = (view: 'comics' | 'lists') => {
        if (view === 'lists') {
            navigate("/lists");
        } else {
            navigate("/");
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
            <FloatingHeader
                currentView="comics"
                onViewChange={handleViewChange}
                onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
                isMenuOpen={isMenuOpen}
                menuRef={menuRef}
                menuItems={renderMenuItems(t, setIsSettingsModalOpen, setIsLLMConfigModalOpen, setIsMaintenanceModalOpen, setIsAboutModalOpen, setIsPasswordChangeModalOpen, setIsFieldVisibilityModalOpen, handleLogout, setIsMenuOpen)}
                showSearch={true}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                showFilters={true}
                currentFilter={filter}
                onFilterChange={setFilter}
            />
            <div className="pt-44 min-[800px]:pt-16">

                <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <ComicGrid
                        key={settingsVersion}
                        comics={comics}
                        onComicUpdate={() => {
                            // Fetch both comics and lists to ensure dynamic lists reflect changes
                            fetchComics(sortBy === 'custom');
                            fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
                        }}
                        onEditClick={handleEditClick}
                        externalSearchQuery={searchQuery}
                        externalFilter={filter}
                        showSorting={true}
                        externalSortBy={sortBy}
                        onSortChange={setSortBy}
                        onCustomReorder={handleCustomReorder}
                    />
                </main>

                <AddComicModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onComicAdded={handleComicAdded}
                />

                <EditComicModal
                    isOpen={isEditModalOpen}
                    onClose={handleEditModalClose}
                    onComicUpdated={handleComicUpdated}
                    comic={editingComic}
                />

                <SettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                    onSettingsChanged={handleSettingsChanged}
                />

                <MaintenanceModal
                    isOpen={isMaintenanceModalOpen}
                    onClose={() => setIsMaintenanceModalOpen(false)}
                    onComicsUpdated={() => {
                        // Fetch both comics and lists to ensure dynamic lists reflect changes
                        fetchComics(sortBy === 'custom');
                        fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
                    }}
                />

                <AboutModal
                    isOpen={isAboutModalOpen}
                    onClose={() => setIsAboutModalOpen(false)}
                />

                <PasswordChangeModal
                    isOpen={isPasswordChangeModalOpen}
                    onClose={() => setIsPasswordChangeModalOpen(false)}
                />

                <FieldVisibilityModal
                    isOpen={isFieldVisibilityModalOpen}
                    onClose={() => setIsFieldVisibilityModalOpen(false)}
                />

                <LLMConfigurationsModal
                    isOpen={isLLMConfigModalOpen}
                    onClose={() => setIsLLMConfigModalOpen(false)}
                />

                <ConfettiAnimation 
                    isActive={showConfetti}
                    onComplete={() => setShowConfetti(false)}
                />

                <ListEditor
                    isOpen={isListEditorOpen}
                    onClose={() => {
                        setIsListEditorOpen(false);
                        setEditingList(null);
                    }}
                    onListSaved={handleListSaved}
                    list={editingList}
                />

                {/* Floating Add Button */}
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center text-2xl font-light"
                    title={t('list.addComic')}
                >
                    +
                </button>
            </div>
        </div>
    );
}

// Lists page component
function ListsPage() {
    const navigate = useNavigate();
    const { listId } = useParams();
    const { t } = useTranslation();
    const { refreshSettings } = useSettings();
    const { lists, setComics, fetchComics, fetchLists, loading } = useComicsData();
    const [selectedList, setSelectedList] = useState<CustomList | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isListEditorOpen, setIsListEditorOpen] = useState(false);
    const [editingComic, setEditingComic] = useState<Comic | null>(null);
    const [editingList, setEditingList] = useState<CustomList | null>(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [, setSettingsVersion] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
    const [isFieldVisibilityModalOpen, setIsFieldVisibilityModalOpen] = useState(false);
    const [isLLMConfigModalOpen, setIsLLMConfigModalOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Handle list selection from URL parameter
    useEffect(() => {
        if (listId && lists.length > 0) {
            const list = lists.find(l => l.id === parseInt(listId));
            setSelectedList(list || null);
        } else {
            setSelectedList(null);
        }
    }, [listId, lists]);


    const handleComicAdded = () => {
        // Fetch both comics and lists to ensure dynamic lists include new comics
        fetchComics();
        fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
        setIsAddModalOpen(false);
    };

    const handleEditClick = async (comic: Comic) => {
        try {
            const comicWithRoles = await getComic(comic.id);
            setEditingComic(comicWithRoles);
            setIsEditModalOpen(true);
        } catch (error) {
            
            setEditingComic(comic);
            setIsEditModalOpen(true);
        }
    };

    const handleComicUpdated = (shouldShowConfetti?: boolean) => {
        // Fetch both comics and lists to ensure dynamic lists reflect changes
        fetchComics();
        fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
        setIsEditModalOpen(false);
        setEditingComic(null);
        
        // Trigger confetti if requested
        if (shouldShowConfetti) {
            setShowConfetti(true);
        }
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setEditingComic(null);
    };

    const handleLogout = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        setComics([]);
        window.location.reload();
    };

    const handleSettingsChanged = async () => {
        try {
            await refreshSettings();
        } catch (error) {
            
        }

        setSettingsVersion((prev) => prev + 1);
    };

    const handleListSaved = (savedListId?: number) => {
        fetchLists();
        // Navigate to the newly created/saved list
        if (savedListId) {
            navigate(`/lists/${savedListId}`);
        }
    };

    const handleEditList = (list: CustomList) => {
        setEditingList(list);
        setIsListEditorOpen(true);
    };

    const handleDeleteList = async (list: CustomList) => {
        if (
            window.confirm(
                t('common.areYouSureDeleteList', { listName: list.name })
            )
        ) {
            try {
                await deleteCustomList(list.id);
                fetchLists();
                if (selectedList?.id === list.id) {
                    setSelectedList(null);
                    navigate("/lists");
                }
            } catch (error) {
                
            }
        }
    };

    const handleCopyList = async (list: CustomList) => {
        const newName = window.prompt(
            t('list.enterNewName'),
            `${list.name} - Copy`
        );
        
        if (newName && newName.trim()) {
            try {
                await copyCustomList(list.id, newName.trim());
                fetchLists();
            } catch (error) {
                
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-lg text-gray-900 dark:text-gray-100">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    const handleViewChange = (view: 'comics' | 'lists') => {
        if (view === 'comics') {
            navigate("/");
        } else {
            navigate("/lists");
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
            <FloatingHeader
                currentView="lists"
                onViewChange={handleViewChange}
                onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
                isMenuOpen={isMenuOpen}
                menuRef={menuRef}
                menuItems={renderMenuItems(t, setIsSettingsModalOpen, setIsLLMConfigModalOpen, setIsMaintenanceModalOpen, setIsAboutModalOpen, setIsPasswordChangeModalOpen, setIsFieldVisibilityModalOpen, handleLogout, setIsMenuOpen)}
                lists={lists}
                selectedListId={selectedList?.id || null}
                onListChange={(listId) => {
                    if (listId) {
                        navigate(`/lists/${listId}`);
                    } else {
                        navigate("/lists");
                    }
                }}
            />
            <div className="pt-44 min-[800px]:pt-16">
                <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {selectedList ? (
                    <ListView
                        list={selectedList}
                        onEditClick={handleEditList}
                        onDeleteClick={handleDeleteList}
                        onCopyClick={handleCopyList}
                        onComicClick={handleEditClick}
                        onListUpdate={fetchLists}
                    />
                ) : (
                    <div className="text-center py-12">
                        <div className="text-gray-400 dark:text-gray-500 mb-4">
                            <svg
                                className="h-12 w-12 mx-auto"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            {t('list.noListSelected')}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {t('list.selectListPrompt')}
                        </p>
                    </div>
                )}
            </main>

            <AddComicModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onComicAdded={handleComicAdded}
            />

            <EditComicModal
                isOpen={isEditModalOpen}
                onClose={handleEditModalClose}
                onComicUpdated={handleComicUpdated}
                comic={editingComic}
            />

            <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                onSettingsChanged={handleSettingsChanged}
            />

            <MaintenanceModal
                isOpen={isMaintenanceModalOpen}
                onClose={() => setIsMaintenanceModalOpen(false)}
                onComicsUpdated={() => {
                    // Fetch both comics and lists to ensure dynamic lists reflect changes
                    fetchComics();
                    fetchLists(); // This ensures dynamic lists re-evaluate their filter criteria
                }}
            />

            <AboutModal
                isOpen={isAboutModalOpen}
                onClose={() => setIsAboutModalOpen(false)}
            />

            <PasswordChangeModal
                isOpen={isPasswordChangeModalOpen}
                onClose={() => setIsPasswordChangeModalOpen(false)}
            />

            <FieldVisibilityModal
                isOpen={isFieldVisibilityModalOpen}
                onClose={() => setIsFieldVisibilityModalOpen(false)}
            />

            <LLMConfigurationsModal
                isOpen={isLLMConfigModalOpen}
                onClose={() => setIsLLMConfigModalOpen(false)}
            />

            <ConfettiAnimation 
                isActive={showConfetti}
                onComplete={() => setShowConfetti(false)}
            />

            <ListEditor
                isOpen={isListEditorOpen}
                onClose={() => {
                    setIsListEditorOpen(false);
                    setEditingList(null);
                }}
                onListSaved={handleListSaved}
                list={editingList}
            />

            {/* Floating Add Button */}
            <button
                onClick={() => {
                    setEditingList(null);
                    setIsListEditorOpen(true);
                }}
                className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center text-2xl font-light"
                title={t('list.createList')}
            >
                +
            </button>
            </div>
        </div>
    );
}

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [hasUsers, setHasUsers] = useState(true);
    const [showFirstTimeSetup, setShowFirstTimeSetup] = useState(false);
    const { refreshAfterLogin: refreshComicsAfterLogin } = useComicsData();
    const { refreshAfterLogin: refreshSettingsAfterLogin } = useSettings();
    const { refreshAfterLogin: refreshReferenceDataAfterLogin } = useReferenceData();
    const { refreshAfterLogin: refreshFieldVisibilityAfterLogin } = useFieldVisibility();

    useEffect(() => {
        const checkAuthAndUsers = async () => {
            try {
                // First check if any users exist
                const usersResponse = await checkUsersExist();
                setHasUsers(usersResponse.has_users);
                
                if (!usersResponse.has_users) {
                    // No users exist, show first-time setup
                    setShowFirstTimeSetup(true);
                    setLoading(false);
                    return;
                }
                
                // Users exist, check authentication
                setAuthenticated(isAuthenticated());
                setLoading(false);
            } catch (error) {
                
                // On error, assume users exist and proceed with normal auth flow
                setHasUsers(true);
                setAuthenticated(isAuthenticated());
                setLoading(false);
            }
        };
        
        checkAuthAndUsers();
    }, []);

    // Listen for authentication invalidation events
    useEffect(() => {
        const handleAuthInvalidated = () => {
            setAuthenticated(false);
            setLoading(false);
        };

        window.addEventListener('auth-invalidated', handleAuthInvalidated);
        
        return () => {
            window.removeEventListener('auth-invalidated', handleAuthInvalidated);
        };
    }, []);

    const handleLoginSuccess = async () => {
        setAuthenticated(true);
        await Promise.all([
            refreshComicsAfterLogin(),
            refreshSettingsAfterLogin(),
            refreshReferenceDataAfterLogin(),
            refreshFieldVisibilityAfterLogin()
        ]);
    };

    const handleFirstTimeSetupComplete = async () => {
        setShowFirstTimeSetup(false);
        setHasUsers(true);
        // After creating first user, they still need to login normally
        setAuthenticated(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-lg text-gray-900 dark:text-gray-100">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    if (showFirstTimeSetup && !hasUsers) {
        return <FirstTimeSetup onSetupComplete={handleFirstTimeSetupComplete} />;
    }

    if (!authenticated) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    return <>{children}</>;
}

// Component to handle document meta updates at the top level
function DocumentMetaUpdater() {
    useDocumentMeta();
    return null;
}

// Main App component with providers and routing
function App() {
    return (
        <ThemeProvider>
            <SettingsProvider>
                <FieldVisibilityProvider>
                    <ReferenceDataProvider>
                        <ComicsDataProvider>
                        <DocumentMetaUpdater />
                        <Router basename={getWebroot()}>
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <ComicsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/lists"
                            element={
                                <ProtectedRoute>
                                    <ListsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/lists/:listId"
                            element={
                                <ProtectedRoute>
                                    <ListsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route 
                            path="/l/:shareToken" 
                            element={
                                <PublicThemeProvider>
                                    <PublicListView />
                                </PublicThemeProvider>
                            } 
                        />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Router>
                        </ComicsDataProvider>
                    </ReferenceDataProvider>
                </FieldVisibilityProvider>
            </SettingsProvider>
        </ThemeProvider>
    );
}

export default App;
